/**
 * The portfolio, grouped by programme.
 *
 * The list reads as one row per programme rather than one per approved
 * revision: a programme with four revisions was four rows repeating the same
 * title, and the reader's question is which one is in force today. The rest
 * become that row's curriculum history.
 *
 * @module components/portfolio/portfolio-groups
 */

import type { PortfolioProgrammeRevision } from "@/platform";

/** One programme's rows, and the revision that heads them. */
export interface ProgrammeGroup {
  programmeId: string;
  /** Every row for this programme, including ones the filters excluded. */
  revisions: readonly PortfolioProgrammeRevision[];
  /** The revision in force, or the closest to it. */
  primary: PortfolioProgrammeRevision;
}

/**
 * Which lifecycle heads a programme.
 *
 * The row reports what is in force today, so a future revision sorts below a
 * current one however much higher its number: it belongs in the history, not
 * in the headline.
 */
const LIFECYCLE_ORDER: Record<string, number> = {
  current: 0,
  "approved-future": 1,
  superseded: 2,
};

function lifecycleOrder(revision: PortfolioProgrammeRevision): number {
  return LIFECYCLE_ORDER[revision.lifecycleStatus] ?? Number.MAX_SAFE_INTEGER;
}

/** `2.10` sorts above `2.9`, which a string compare gets backwards. */
export function revisionNumber(revision: PortfolioProgrammeRevision): number {
  const [major = 0, minor = 0] = revision.revisionNumber.split(".").map(Number);
  return major * 1_000 + minor;
}

/**
 * The distinct approved revisions in a programme's rows, newest first.
 *
 * The portfolio emits one row per applicability scope, so a revision holding
 * two intakes arrives twice. Each entry is that revision's scopes together.
 */
export function uniqueApprovedRevisions(
  revisions: readonly PortfolioProgrammeRevision[],
): PortfolioProgrammeRevision[][] {
  const grouped = new Map<string, PortfolioProgrammeRevision[]>();
  revisions.forEach((revision) => {
    grouped.set(revision.curriculumRevisionId, [
      ...(grouped.get(revision.curriculumRevisionId) ?? []),
      revision,
    ]);
  });
  return [...grouped.values()].sort(
    (left, right) => revisionNumber(right[0]!) - revisionNumber(left[0]!),
  );
}

/**
 * Group the portfolio by programme, keeping each programme's full history.
 *
 * Two lists, deliberately. A programme appears when *any* of its rows matches
 * the filters, but its history then shows every revision it has — filtering to
 * "approved future" and being told the programme has one revision would be a
 * false answer to a question about the filter.
 *
 * @param allRevisions - Every row in the portfolio.
 * @param matchingRevisions - The rows the filters kept.
 */
export function groupPortfolioProgrammes(
  allRevisions: readonly PortfolioProgrammeRevision[],
  matchingRevisions: readonly PortfolioProgrammeRevision[],
): ProgrammeGroup[] {
  const matchingProgrammeIds = new Set(matchingRevisions.map((revision) => revision.programmeId));
  const grouped = new Map<string, PortfolioProgrammeRevision[]>();
  const matchingGrouped = new Map<string, PortfolioProgrammeRevision[]>();
  matchingRevisions.forEach((revision) => {
    matchingGrouped.set(revision.programmeId, [
      ...(matchingGrouped.get(revision.programmeId) ?? []),
      revision,
    ]);
  });
  allRevisions.forEach((revision) => {
    if (matchingProgrammeIds.has(revision.programmeId)) {
      grouped.set(revision.programmeId, [...(grouped.get(revision.programmeId) ?? []), revision]);
    }
  });
  return [...grouped.entries()]
    .map(([programmeId, revisions]) => {
      // Headed by a revision the filters kept, not just any: a row leading
      // with a revision the reader has filtered out reads as a mistake.
      const ordered = [...(matchingGrouped.get(programmeId) ?? revisions)].sort(
        (left, right) =>
          lifecycleOrder(left) - lifecycleOrder(right) ||
          revisionNumber(right) - revisionNumber(left),
      );
      return { programmeId, revisions, primary: ordered[0]! };
    })
    .sort((left, right) => left.primary.title.localeCompare(right.primary.title));
}
